import * as types from '../types.js';
import api from '../api.js';

/**
 * Rótulo PT da natureza operacional (opt_movement_kind).
 * @param {string} code
 * @returns {string}
 */
export function movementKindLabel(code)
{
	const c = String(code || '').trim();
	if (!c)
		return '';
	const opts = types.opt_movement_kind()?.options || [];
	const hit = opts.find((o) => o.value === c);
	return hit ? String(hit.label) : c;
}

/**
 * Valor gravado em source.reason a partir da natureza selecionada
 * (mantém compatibilidade com APIs/relatórios que leem o motivo em texto).
 * @param {string} code
 * @param {HTMLInputElement|null} [kindEl]
 * @returns {string}
 */
export function reasonFromMovementKind(code, kindEl = null)
{
	const c = String(code || '').trim();
	if (kindEl?.list?.options)
	{
		const opts = Array.from(kindEl.list.options);
		const hit = opts.find((o) => String(o.value).trim() === c);
		if (hit)
			return String(hit.label || hit.textContent || c).trim() || c;
	}
	return movementKindLabel(code);
}

/**
 * Resolve o código da natureza a partir do valor exibido no input (datalist).
 * @param {HTMLInputElement|null} kindEl
 * @returns {string}
 */
export function resolveMovementKindCode(kindEl)
{
	if (!kindEl)
		return '';
	const raw = String(kindEl.value || '').trim();
	if (!raw)
		return '';
	const opts = kindEl.list?.options ? Array.from(kindEl.list.options) : (types.opt_movement_kind()?.options || []);
	const byValue = opts.filter((o) => String(o.value).trim() === raw);
	if (byValue.length === 1)
		return String(byValue[0].value).trim();
	const byLabel = opts.filter((o) => String(o.label || o.innerText || o.textContent || '').trim() === raw);
	if (byLabel.length === 1)
		return String(byLabel[0].value).trim();
	return raw;
}

/**
 * #MT-6BF7122 — carrega naturezas ativas do tenant no datalist (rótulo editável).
 * @param {string} companyId
 * @param {HTMLDataListElement|null} listEl
 */
export async function loadCompanyBlockKinds(companyId, listEl)
{
	if (!listEl || !companyId)
		return;
	try
	{
		const rows = await api.call(api.list_stock_block_kinds_by_company, companyId, false);
		if (!Array.isArray(rows) || !rows.length)
			return;
		listEl.innerHTML = '';
		for (const r of rows)
		{
			const opt = document.createElement('option');
			opt.value = String(r.code);
			opt.label = String(r.label || r.code);
			opt.textContent = String(r.label || r.code);
			listEl.appendChild(opt);
		}
	}
	catch (_)
	{
		/* mantém options padrão do opt_movement_kind */
	}
}

/**
 * Motivo = natureza selecionada; campo Motivo fica somente leitura.
 * @param {object} formset
 * @param {{ kindId?: string, reasonId?: string }} [opts]
 */
export function syncBlockReasonFromKind(formset, opts = {})
{
	const kindId = opts.kindId || 'movement_kind';
	const reasonId = opts.reasonId || 'reason';
	const root = formset?.form?.node || document.querySelector(formset?.form?.selector || '');
	if (!root)
		return '';
	const kindEl = root.querySelector(`#${kindId}`);
	const reasonEl = root.querySelector(`#${reasonId}`);
	const code = resolveMovementKindCode(kindEl);
	const reason = reasonFromMovementKind(code, kindEl);
	if (reasonEl)
	{
		reasonEl.value = reason;
		reasonEl.setAttribute('readonly', 'readonly');
		reasonEl.tabIndex = -1;
	}
	if (formset?.form?.setValues)
		formset.form.setValues({ [reasonId]: reason, ...(code ? { [kindId]: code } : {}) });
	return reason;
}

/**
 * Liga change/input da Natureza → Motivo (readonly).
 * @param {object} formset
 * @param {{ kindId?: string, reasonId?: string }} [opts]
 * @returns {() => void} unbind
 */
export function bindBlockReasonFromKind(formset, opts = {})
{
	const kindId = opts.kindId || 'movement_kind';
	const root = formset?.form?.node || document.querySelector(formset?.form?.selector || '');
	if (!root)
		return () => {};
	const kindEl = root.querySelector(`#${kindId}`);
	const reasonEl = root.querySelector(`#${opts.reasonId || 'reason'}`);
	if (reasonEl)
	{
		reasonEl.setAttribute('readonly', 'readonly');
		reasonEl.tabIndex = -1;
		reasonEl.removeAttribute('required');
		reasonEl.placeholder = 'Preenchido automaticamente pela Natureza';
	}
	const companyId = root.querySelector('#company_id')?.value
		|| formset?.form?.getFieldBySelector?.('#company_id')?.getValue?.();
	const listEl = kindEl?.list || root.querySelector('#movement_kind_list');
	if (companyId && listEl)
		loadCompanyBlockKinds(companyId, listEl);
	const sync = () => syncBlockReasonFromKind(formset, opts);
	kindEl?.addEventListener('change', sync);
	kindEl?.addEventListener('input', sync);
	sync();
	return () =>
	{
		kindEl?.removeEventListener('change', sync);
		kindEl?.removeEventListener('input', sync);
	};
}
